// How far a brand has got, in the two vocabularies the backend reports it in.
// Both are expected to change, so they are named once here and nowhere else.
//
// See "Extraction - API Contract" on Confluence (CONTENT/1365937796).

// `brand_lifecycle_status`, carried on every brand in GET /brands and again
// on GET /brands/{brandUuid}/details. This is what the Drafts tab runs on:
// it decides which screen to show and whether there is anything to poll for.
export const BRAND_LIFECYCLE_STATUS = Object.freeze({
  // Uploading and validating documents. Nothing has been extracted.
  DRAFT: 'DRAFT',
  EXTRACTION_IN_PROGRESS: 'EXTRACTION_IN_PROGRESS',
  IN_REVIEW: 'IN_REVIEW',
  // Every section is decided, but the brand has not been handed over yet —
  // so the rail still offers Send to Publish Queue, exactly as in IN_REVIEW.
  REVIEW_COMPLETED: 'REVIEW_COMPLETED',
  // Sent. The review becomes a read-only record.
  AWAITING_PUBLISH: 'AWAITING_PUBLISH',
  PUBLISHED: 'PUBLISHED',
  // Live. The sections now come from the brand profile, not the drafts.
  ACTIVE: 'ACTIVE',
})

export const isActive = (lifecycleStatus) =>
  lifecycleStatus === BRAND_LIFECYCLE_STATUS.ACTIVE

export const isAwaitingPublish = (lifecycleStatus) =>
  lifecycleStatus === BRAND_LIFECYCLE_STATUS.AWAITING_PUBLISH ||
  lifecycleStatus === BRAND_LIFECYCLE_STATUS.PUBLISHED

// Once every section has a decision, the review is a read-only record even
// before it is handed to the publish queue.
export const isReviewSettled = (lifecycleStatus) =>
  lifecycleStatus === BRAND_LIFECYCLE_STATUS.REVIEW_COMPLETED ||
  isAwaitingPublish(lifecycleStatus)

// Uploading, validating and extracting all belong to DRAFT. Past it the
// documents are settled and the rail has moved on to the sections.
export const isDraft = (lifecycleStatus) =>
  lifecycleStatus === BRAND_LIFECYCLE_STATUS.DRAFT

// Extraction is running against this brand. The only state in which the
// progress screen is shown and the status endpoint is worth polling.
export const isExtractionPhase = (lifecycleStatus) =>
  lifecycleStatus === BRAND_LIFECYCLE_STATUS.EXTRACTION_IN_PROGRESS

// Sections exist and are worth fetching — either waiting to be reviewed, or
// kept as a record afterwards. Someone arriving from the landing page on one
// of these goes straight to the sections list.
export const hasSections = (lifecycleStatus) =>
  lifecycleStatus === BRAND_LIFECYCLE_STATUS.IN_REVIEW ||
  lifecycleStatus === BRAND_LIFECYCLE_STATUS.REVIEW_COMPLETED ||
  isAwaitingPublish(lifecycleStatus)

const LIFECYCLE_ORDER = Object.freeze([
  BRAND_LIFECYCLE_STATUS.DRAFT,
  BRAND_LIFECYCLE_STATUS.EXTRACTION_IN_PROGRESS,
  BRAND_LIFECYCLE_STATUS.IN_REVIEW,
  BRAND_LIFECYCLE_STATUS.REVIEW_COMPLETED,
  BRAND_LIFECYCLE_STATUS.AWAITING_PUBLISH,
  BRAND_LIFECYCLE_STATUS.PUBLISHED,
  BRAND_LIFECYCLE_STATUS.ACTIVE,
])

// The lifecycle only arrives with a brands-list read, so a status this
// session has just caused can be ahead of the one it last saw. Whichever is
// further along wins; an unrecognised status never displaces a known one.
export const getFurthestLifecycleStatus = (first, second) =>
  LIFECYCLE_ORDER.indexOf(second) > LIFECYCLE_ORDER.indexOf(first)
    ? second
    : first

// `status` on the extraction job, from GET /brands/{brandUuid}/extraction/status.
// This is the one the Drafts tab actually runs on: it is the dedicated poll
// target, and it answers "has this brand ever extracted?" without a 404.
export const EXTRACTION_STATUS = Object.freeze({
  // A brand that has never run extraction. Returned as 200, not 404, so the
  // Drafts tab can poll unconditionally.
  NOT_STARTED: 'NOT_STARTED',
  QUEUED: 'QUEUED',
  SUBMITTED: 'SUBMITTED',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
})

// The contract says COMPLETED and the deployed service has also answered
// COMPLETE. Both are accepted so a rename cannot leave the reviewer watching
// a finished job with the Review button still dark.
const COMPLETED_EXTRACTION_STATUSES = Object.freeze([
  EXTRACTION_STATUS.COMPLETED,
  'COMPLETE',
])

// Extraction has finished and there are sections behind the Review button.
export const isExtractionComplete = (status) =>
  COMPLETED_EXTRACTION_STATUSES.includes(status)

// Nothing more will happen on its own in these states, so polling stops.
const TERMINAL_EXTRACTION_STATUSES = Object.freeze([
  EXTRACTION_STATUS.NOT_STARTED,
  ...COMPLETED_EXTRACTION_STATUSES,
  EXTRACTION_STATUS.FAILED,
  EXTRACTION_STATUS.CANCELLED,
])

// The backend is working on it. Drives the "Extraction in progress" screen.
export const isExtractionRunning = (status) =>
  Boolean(status) && !TERMINAL_EXTRACTION_STATUSES.includes(status)

// True once extraction has been kicked off at all. Until then the Drafts tab
// is empty and the work is still on the Upload Documents tab.
export const hasExtractionStarted = (status) =>
  Boolean(status) && status !== EXTRACTION_STATUS.NOT_STARTED

export const hasExtractionFailed = (status) =>
  status === EXTRACTION_STATUS.FAILED

// Per-file status inside the extraction status payload, used only to tell a
// file still being worked through from one that is done.
export const EXTRACTION_FILE_STATUS = Object.freeze({
  EXTRACTING: 'EXTRACTING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
})

export const isFileExtracting = (status) =>
  status !== EXTRACTION_FILE_STATUS.COMPLETED &&
  status !== EXTRACTION_FILE_STATUS.FAILED

// Fallback cadence only. The server returns `poll_after_ms` on every status
// response so the beat can be retuned without a UI release.
export const DEFAULT_POLL_INTERVAL_MS = 10000
// Guards against a server value that would hammer the endpoint or stall the
// screen, whatever it sends.
export const MIN_POLL_INTERVAL_MS = 2000
export const MAX_POLL_INTERVAL_MS = 60000

// A status endpoint that is down should not be asked every five seconds for
// as long as the tab is open, so each consecutive failure doubles the wait.
const MAX_BACKOFF_DOUBLINGS = 4

export const toPollInterval = (pollAfterMs, consecutiveFailures = 0) => {
  const base = Number(pollAfterMs) || DEFAULT_POLL_INTERVAL_MS
  const interval =
    base * 2 ** Math.min(consecutiveFailures, MAX_BACKOFF_DOUBLINGS)
  return Math.min(
    Math.max(interval, MIN_POLL_INTERVAL_MS),
    MAX_POLL_INTERVAL_MS,
  )
}

export const EXTRACTION_ERROR_FALLBACK =
  'Extraction failed. Re-run it, or contact support if it keeps failing.'

// Taken off undo_seconds_remaining so a cancel sent near the end still
// reaches the backend before its undo window closes.
export const UNDO_SAFETY_MARGIN_SECONDS = 5

// `status` on GET /publications/current. The brand's own lifecycle status
// lags behind it, so this is what decides whether a brand is already queued.
export const PUBLICATION_STATUS = Object.freeze({
  CANCELLED: 'CANCELLED',
  FAILED: 'FAILED',
  RETRY_EXHAUSTED: 'RETRY_EXHAUSTED',
})

// A brand never sent has no publication (the endpoint answers 404), and a
// cancelled one leaves it exactly as if it had never been sent.
export const hasOpenPublication = (publication) =>
  Boolean(publication) && publication.status !== PUBLICATION_STATUS.CANCELLED

const normalizePublicationStatus = (publication) =>
  `${publication?.status ?? ''}`.trim().toUpperCase()

const normalizeAttemptStatus = (publication) =>
  `${publication?.attempt_status ?? ''}`.trim().toUpperCase()

const getPublicationAttemptNumber = (publication) =>
  Number(publication?.attempt_number) || 0

const getPublicationMaxAttempts = (publication) =>
  Number(publication?.max_attempts) || 0

const isRetryAvailablePublication = (publication) =>
  publication?.retry_available === true &&
  (normalizePublicationStatus(publication) === 'RETRY_AVAILABLE' ||
    normalizeAttemptStatus(publication) === 'RETRY_AVAILABLE' ||
    normalizePublicationStatus(publication) === PUBLICATION_STATUS.FAILED ||
    normalizeAttemptStatus(publication) === PUBLICATION_STATUS.FAILED)

// Out of retries: only an admin can recover it from here.
export const isRetryExhausted = (publication) =>
  publication?.status === PUBLICATION_STATUS.RETRY_EXHAUSTED ||
  publication?.attempt_status === PUBLICATION_STATUS.RETRY_EXHAUSTED ||
  (isRetryAvailablePublication(publication) &&
    getPublicationAttemptNumber(publication) >=
      getPublicationMaxAttempts(publication)) ||
  (publication?.status === PUBLICATION_STATUS.FAILED &&
    publication.retry_available !== true)

export const hasPublicationFailed = (publication) =>
  normalizePublicationStatus(publication) === PUBLICATION_STATUS.FAILED ||
  normalizePublicationStatus(publication) === 'RETRY_AVAILABLE' ||
  normalizeAttemptStatus(publication) === PUBLICATION_STATUS.FAILED ||
  normalizeAttemptStatus(publication) === 'RETRY_AVAILABLE' ||
  isRetryExhausted(publication)

// The review is read-only once it has been sent. A fully approved review that
// has not been sent (or whose publication was cancelled) stays open, so its
// sections can still be undone or edited.
export const isReviewLocked = (lifecycleStatus, publication) =>
  isAwaitingPublish(lifecycleStatus) ||
  (lifecycleStatus === BRAND_LIFECYCLE_STATUS.REVIEW_COMPLETED &&
    hasOpenPublication(publication))

// How often a queued publication is asked about again, so the rail moves on
// to Republish, Recover or a live brand without a refresh.
export const PUBLICATION_POLL_INTERVAL_MS = 10000

export const REPUBLISH_COPY = Object.freeze({
  hint: 'The last publish did not finish. Send the brand again.',
  label: 'Republish',
  recoverHint: 'Retry limit reached. Recover the publication to try again.',
  recoverLabel: 'Recover',
  exhausted:
    'Retry limit reached. Contact admins for help publishing this brand.',
})
